import {MiniSignal, texture, DEG_TO_RAD, json} from "p3";
import { Emitter } from "pixi-particles";
import BossFightScene from "../../scenes/BossFightScene";

export default class ActionWheel extends PIXI.Container {
    /**
     * @param {number} spinSpeed 
     * @param {number} minDamage 
     * @param {number} maxDamage 
     */
    constructor(spinSpeed, minDamage, maxDamage, attack, healing, miss) {
        super();

        this._min = minDamage;
        this._max = maxDamage;

        this._attack = attack;
        this._heal = healing;
        this._miss = miss;

        this.signals = {};

        this._segmentContainer = null;
        this._defaultTween = null;
        this._meterBg = null;
        this._marker = null;
        this._attackMarker = null;
        this._attackSegment = null;
        this._healMarker = null;
        this._healSegment = null;
        this._missSegment = null;
        this._missMarker = null;
        this._circleSword = null;
        this._circleHeal = null;

        this._attackPerc = null;
        this._healPerc = null;
        this._missPerc = null;

        this._missFx = null;
        this._healFx = null;
        this._attackFx = null;

        this._distanceFromCenter = 35;
        this._damage = 0;
        this._isAttacking = false;
        this._enabled = false;

        this._initialSpinVelocity = spinSpeed;
        this._spinVelocity = spinSpeed;
        this._spinSpeed = 1;
        this._spinSpeedMax = 15;

        this.signals.attack = new MiniSignal();
        this.signals.heal = new MiniSignal();
        this.signals.miss = new MiniSignal();

        this._onMouseClickBinding = null;

        this.init();
    }

    init() {
        this._meterBg = new PIXI.Sprite(texture("weapon_spinner_circle_bg"));
        this._meterBg.anchor.set(0.5);
        this._meterBg.rotation = 90 * DEG_TO_RAD;
        this.addChild(this._meterBg);

        this._circleSword = new PIXI.Sprite(texture("weapon_spinner_circle_sword"));
        this._circleSword.anchor.set(0.5);
        this._circleSword.rotation = 90 * DEG_TO_RAD;
        this.addChild(this._circleSword);

        this._circleHeal = new PIXI.Sprite(texture("weapon_spinner_circle_turnip"));
        this._circleHeal.anchor.set(0.5);
        this._circleHeal.rotation = 90 * DEG_TO_RAD;
        this.addChild(this._circleHeal);

        this._attackMarker = new PIXI.Sprite(texture("weapon_spinner_icon_sword"));
        this._attackMarker.anchor.set(0.5);
        this._attackMarker.rotation = 90 * DEG_TO_RAD;

        this._healMarker = new PIXI.Sprite(texture("weapon_spinner_icon_turnip"));
        this._healMarker.anchor.set(0.5);
        this._healMarker.rotation = 90 * DEG_TO_RAD;

        this._missMarker = new PIXI.Sprite(texture("weapon_spinner_empty"));
        this._missMarker.anchor.set(0.5);
        this._missMarker.rotation = 90 * DEG_TO_RAD;

        this._initSegments();

        this._marker = new PIXI.Sprite(texture("weapon_spinner_arrow"));
        this._marker.anchor.set(0.5);
        this._marker.pivot.set(this._meterBg.x, this._meterBg.height / 2. - this._distanceFromCenter);
        this.addChild(this._marker);

        this.alpha = 0.;
        this.scale.set(0.5);
    }

    update() {
        this._fx && this._fx.update(window.dt);

        if(this._marker && !this._isAttacking) {
            this._marker.rotation += this._spinVelocity * window.dt;

            if(this._marker.rotation % (360 * DEG_TO_RAD) < 1.0) {
                if(this._spinVelocity >= this._spinSpeedMax) {
                    this._spinVelocity = this._spinSpeedMax;
                } else {
                    this._spinVelocity += this._spinSpeed * window.dt;
                }
            }
        }

        if (P3.input.isKeyPressed(P3.keys.SPACE)) {
            this.onMouseClick();
        }
    }

    destroy() {
        this.stopAnimations();

        super.destroy({children:true});
    }

    _createFx(textures = [], data) {
        const emitter = new Emitter(this._segmentContainer, textures, json(data));
        emitter.autoUpdate = false;
        emitter.updateSpawnPos(this.width / 2, this.height / 2);
        return emitter;
    }

    animateIn() {
        return new Promise((resolve) => {
            this._spinVelocity = this._initialSpinVelocity;
            this._isAttacking = false;

            this._defaultTween = gsap.timeline();
            BossFightScene.active.addTween(this._defaultTween);

            this._defaultTween.add(gsap.to(this.scale, 0.5, {
                x: 1.,
                y: 1.,
                ease: Back.easeInOut,
                onComplete: () => {}
            }), 0);

            this._defaultTween.add(gsap.to(this, 0.5, {
                alpha: 1.,
                ease: Back.easeInOut,
                onComplete: () => {
                    resolve();
                }
            }), 0);
        });
    }

    animateOut() {
        return new Promise((resolve) => {
            this._enabled = false;

            this._defaultTween = gsap.timeline();
            BossFightScene.active.addTween(this._defaultTween);

            this._defaultTween.add(gsap.to(this.scale, 0.5, {
                x: 0.5,
                y: 0.5,
                ease: Back.easeIn,
                onComplete: () => {}
            }), 0);

            this._defaultTween.add(gsap.to(this, 0.5, {
                alpha: 0.,
                ease: Back.easeIn,
                onComplete: () => {
                    resolve();
                }
            }), 0);
        });
    }

    stopAnimations() {
        if (this._defaultTween) {
            BossFightScene.active.removeTween(this._defaultTween, true);
            this._defaultTween = null;
        }
    }

    _initSegments() {
        let wheelRadius = 143;
       
        let circ = this._attack + this._heal + this._miss;
        this._attackPerc = this._attack / circ * 360.;
        this._healPerc = this._heal / circ * 360.;
        this._missPerc = this._miss / circ * 360.;

        let attackTheta = (this._attackPerc * 0.5) * DEG_TO_RAD;
        this._attackMarker.x = Math.cos(attackTheta) * wheelRadius;
        this._attackMarker.y = Math.sin(attackTheta) * wheelRadius;

        let healTheta = (this._attackPerc + this._healPerc * 0.5) * DEG_TO_RAD;
        this._healMarker.x = Math.cos(healTheta) * wheelRadius;
        this._healMarker.y = Math.sin(healTheta) * wheelRadius;

        let missTheta = (this._attackPerc + this._healPerc + this._missPerc * 0.5) * DEG_TO_RAD;
        this._missMarker.x = Math.cos(missTheta) * wheelRadius;
        this._missMarker.y = Math.sin(missTheta) * wheelRadius;

        this._segmentContainer = new PIXI.Container();
        this.addChild(this._segmentContainer);

        this._attackSegment = this._createSegment(wheelRadius, 0x0000ff, 0, this._attackPerc * DEG_TO_RAD, 0.5);
        this._healSegment = this._createSegment(wheelRadius, 0x00ff00, this._attackPerc * DEG_TO_RAD, (this._attackPerc + this._healPerc) * DEG_TO_RAD, 0.5);
        this._missSegment = this._createSegment(wheelRadius, 0x808080, (this._attackPerc + this._healPerc) * DEG_TO_RAD, 360 * DEG_TO_RAD, 0);

        this._circleSword.mask = this._attackSegment;
        this._circleHeal.mask = this._healSegment;

        this._segmentContainer.rotation = -(90. * DEG_TO_RAD);
        
        this._segmentContainer.addChild(this._attackSegment);
        this._segmentContainer.addChild(this._healSegment);
        this._segmentContainer.addChild(this._missSegment);

        this._segmentContainer.addChild(this._attackMarker);
        this._segmentContainer.addChild(this._healMarker);
        this._segmentContainer.addChild(this._missMarker);
    }

    /**
     * @param {number} radius 
     * @param {number} colour 
     * @param {number} startAngle 
     * @param {number} endAngle 
     * @returns 
     */
    _createSegment(radius, colour, startAngle, endAngle, alpha=1) {
        let segment = new PIXI.Graphics()

        segment.lineStyle(22, colour, alpha);
        segment.arc(0, 0, radius, startAngle, endAngle);
        
        return segment;
    }

    _getDamageAmount() {
        let targetPosition = this._attackMarker.toGlobal(this._attackMarker.position);
        let markerPosition = this._marker.toGlobal(this._marker.position);
        let distance = Math.hypot(markerPosition.x - targetPosition.x, markerPosition.y - targetPosition.y); 
        let damage = this._max - (distance * (this._min / (this._min * 50)));

        return parseInt(damage);
    }
 
    onMouseClick() {
        if(this._enabled) {
            this._enabled = false;
            this._isAttacking = true;

            let spinnerAngle = this._marker.angle % 360;
            let attackAngle = this._attackPerc;
            let healAngle = this._attackPerc + this._healPerc;

            if (spinnerAngle >= 0 && spinnerAngle <= attackAngle) {
                this._fx = this._createFx([
                    texture("bossfight_strike_fx_00"),
                    texture("bossfight_strike_fx_01")
                ], "fx_bossfight_btn_strike");
                this._fx.updateSpawnPos(this._attackMarker.x, this._attackMarker.y);

                this.signals.attack.dispatch(this._getDamageAmount());
            }
            else if (spinnerAngle >= this._attackPerc && spinnerAngle <= healAngle) {
                this._fx = this._createFx([
                    texture("bossfight_heal_fx_00"),
                    texture("bossfight_heal_fx_01")
                ], "fx_bossfight_btn_heal");
                this._fx.updateSpawnPos(this._healMarker.x, this._healMarker.y);

                this.signals.heal.dispatch();
            }
            else if (spinnerAngle >= healAngle && spinnerAngle <= 360) {
                this._fx = this._createFx([
                    texture("bossfight_miss_fx_00"),
                    texture("bossfight_miss_fx_01")
                ], "fx_bossfight_btn_miss");
                this._fx.updateSpawnPos(this._missMarker.x, this._missMarker.y);

                this.signals.miss.dispatch();
            } else {
                this._fx = this._createFx([
                    texture("bossfight_miss_fx_00"),
                    texture("bossfight_miss_fx_01")
                ], "fx_bossfight_btn_miss");
                this._fx.updateSpawnPos(this._missMarker.x, this._missMarker.y);

                this.signals.miss.dispatch();
            }
        }
    }

    get enabled() {
        return this._enabled;
    }

    set enabled(value) {
        this._enabled = value;
    }
}